// ReusableSkeleton.tsx
import type { ReactNode } from "react";
import React, { useRef } from "react";
import type { StackProps, GridProps, GroupProps } from "@mantine/core";
import { Skeleton, Stack, Grid, Group } from "@mantine/core";

// Define the possible layout types the skeleton can render
type LayoutType = "stack" | "grid" | "group";

// Define a counter for unique instance IDs
let skeletonInstanceCounter = 0;

/**
 * BaseLayoutProps interface
 *
 * Common properties shared across different layout components.
 */
interface BaseLayoutProps {
  /** Number of skeleton items to render */
  items?: number;
  /** Height of each skeleton item */
  height?: number;
  /** Number of columns for grid layout */
  columns?: number;
  /** Span of each column in grid layout */
  span?: number;
}

/**
 * ReusableSkeletonProps interface
 *
 * Props for the ReusableSkeleton component.
 */
interface ReusableSkeletonProps {
  /** Determines if the skeleton should be displayed */
  isLoading: boolean;
  /** Layout type to use: 'stack', 'grid', or 'group' */
  layout?: LayoutType;
  /** Additional props specific to the chosen layout */
  layoutProps?: (StackProps | GridProps | GroupProps) & BaseLayoutProps;
  /** Custom skeleton elements to render instead of default */
  customSkeletons?: ReactNode;
  /** The actual content to display when not loading */
  children: ReactNode;
}

/**
 * ReusableSkeleton Component
 *
 * A reusable skeleton loader component that displays a loading skeleton
 * based on the specified layout while data is being fetched or processed.
 * Once loading is complete, it renders the provided children.
 *
 * @param {boolean} isLoading - Controls whether to display the skeleton or the children.
 * @param {LayoutType} [layout='stack'] - The layout type for the skeleton: 'stack', 'grid', or 'group'.
 * @param {object} [layoutProps={}] - Additional properties specific to the chosen layout.
 * @param {ReactNode} [customSkeletons] - Custom skeleton elements to render instead of the default ones.
 * @param {ReactNode} children - The content to display when not loading.
 *
 * @returns {ReactNode} - The skeleton loader or the actual content based on the loading state.
 */
const ReusableSkeleton: React.FC<ReusableSkeletonProps> = ({
  isLoading,
  layout = "stack",
  layoutProps = {},
  customSkeletons,
  children,
}) => {
  // Unique instance ID for generating unique keys
  const instanceIdRef = useRef<number>(skeletonInstanceCounter++);

  // If not loading, render the actual content
  if (!isLoading) {
    return <>{children}</>;
  }

  // Function to render the skeleton based on the layout
  const renderSkeleton = () => {
    // If custom skeletons are provided, render them
    if (customSkeletons) {
      return customSkeletons;
    }

    // Render skeletons based on the specified layout
    switch (layout) {
      case "stack": {
        const {
          items = 3,
          height = 20,
          ...stackProps
        } = layoutProps as StackProps & BaseLayoutProps;
        return (
          <Stack {...stackProps}>
            {Array.from({ length: items }).map((_, index) => (
              <Skeleton
                data-testid="reusable-skeleton"
                key={`stack-${instanceIdRef.current}-${index}`}
                height={height}
                radius="sm"
              />
            ))}
          </Stack>
        );
      }
      case "grid": {
        const {
          columns = 3,
          span = 4,
          height = 20,
          ...gridProps
        } = layoutProps as GridProps & BaseLayoutProps;
        return (
          <Grid {...gridProps}>
            {Array.from({ length: columns }).map((_, index) => (
              <Grid.Col
                key={`grid-${instanceIdRef.current}-${index}`}
                span={span}
              >
                <Skeleton height={height} radius="sm" />
              </Grid.Col>
            ))}
          </Grid>
        );
      }
      case "group": {
        const {
          items = 3,
          height = 20,
          ...groupProps
        } = layoutProps as GroupProps & BaseLayoutProps;
        return (
          <Group {...groupProps}>
            {Array.from({ length: items }).map((_, index) => (
              <Skeleton
                key={`group-${instanceIdRef.current}-${index}`}
                height={height}
                radius="sm"
              />
            ))}
          </Group>
        );
      }
      default:
        // Default to a simple stack layout if no valid layout is specified
        return (
          <Stack>
            <Skeleton
              key={`default-${instanceIdRef.current}-1`}
              height={20}
              radius="sm"
            />
            <Skeleton
              key={`default-${instanceIdRef.current}-2`}
              height={20}
              radius="sm"
            />
            <Skeleton
              key={`default-${instanceIdRef.current}-3`}
              height={20}
              radius="sm"
            />
          </Stack>
        );
    }
  };

  // Render the skeleton
  return renderSkeleton();
};

export default ReusableSkeleton;
